import { useState, useCallback } from 'react'
import { supabase } from '../services/supabase'

const ADMIN_EMAIL = 'test@sfpwealth.co.za'
const CACHE_KEY = 'sfp_query_usage_cache'
const CACHE_TTL = 30 * 1000 // 30 seconds

export function useQueryLog() {
  const [usage, setUsage] = useState(null)
  const [loading, setLoading] = useState(false)

  // Log a query to the database (only if user is admin)
  const logQuery = async (queryType, tableName) => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.user?.email) return
      if (session.user.email !== ADMIN_EMAIL) return // Only log for admin

      await supabase.from('query_logs').insert({
        query_type: queryType,
        table_name: tableName,
        user_email: session.user.email,
        user_id: session.user.id,
        cost_estimate: 0.0001
      })
    } catch (error) {
      console.error('Failed to log query:', error)
    }
  }

  // Fetch usage statistics for the current month
  const fetchUsage = useCallback(async (forceRefresh = false) => {
    // Check cache first
    if (!forceRefresh) {
      const cached = localStorage.getItem(CACHE_KEY)
      if (cached) {
        try {
          const { data, timestamp } = JSON.parse(cached)
          if (Date.now() - timestamp < CACHE_TTL) {
            setUsage(data)
            return
          }
        } catch (e) {
          console.error('Cache parse error:', e)
        }
      }
    }

    setLoading(true)
    try {
      // Calculate start of current month
      const startOfMonth = new Date()
      startOfMonth.setDate(1)
      startOfMonth.setHours(0, 0, 0, 0)

      // Fetch all logs for this month
      const { data, error } = await supabase
        .from('query_logs')
        .select('*')
        .gte('created_at', startOfMonth.toISOString())
        .order('created_at', { ascending: false })

      if (error) throw error

      // Calculate statistics
      const totalQueries = data?.length || 0
      const totalCostUSD = totalQueries * 0.0001
      const totalCostZAR = totalCostUSD * 16

      // Group by table name
      const byTable = {}
      const byType = {}

      data?.forEach(row => {
        // Count by table
        byTable[row.table_name] = (byTable[row.table_name] || 0) + 1

        // Count by query type
        byType[row.query_type] = (byType[row.query_type] || 0) + 1
      })

      // Sort tables by query count (descending)
      const tableList = Object.entries(byTable).sort((a, b) => b[1] - a[1])

      // Ensure all query types are present
      const typeList = {
        SELECT: byType.SELECT || 0,
        INSERT: byType.INSERT || 0,
        UPDATE: byType.UPDATE || 0,
        DELETE: byType.DELETE || 0
      }

      const result = {
        totalQueries,
        totalCostUSD: totalCostUSD.toFixed(4),
        totalCostZAR: totalCostZAR.toFixed(2),
        byTable: tableList,
        byType: typeList,
        isHigh: totalQueries >= 50000
      }

      setUsage(result)

      // Cache the result
      localStorage.setItem(CACHE_KEY, JSON.stringify({
        data: result,
        timestamp: Date.now()
      }))
    } catch (error) {
      console.error('Failed to fetch usage:', error)
      setUsage(null)
    } finally {
      setLoading(false)
    }
  }, [])

  return { logQuery, fetchUsage, usage, loading }
}
